import { useCallback, useEffect, useState, type ReactElement } from 'react'
import { Loader2, Pencil, Search, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { toast } from 'sonner'
import {
  useUpdateProductSourceClassification,
  type ProductSource,
} from '@/shared/hooks/api/productSource'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { ProductCatalogIngestionStatus } from '@/features/product-catalog/components/ProductCatalogIngestionStatus'
import { useReuploadProductSourceFile } from '@/features/product-catalog/hooks/useReuploadProductSourceFile'
import { PRODUCT_SOURCE_FILE_ACCEPT } from '@/features/product-catalog/utils/productSourceFileTypes'
import { resolveIngestionFailureText } from '@/features/product-catalog/utils/ingestionFailureMessage'
import { isProductSourceFailed } from '../utils/productChatAvailability'
import {
  isInspectableProductSource,
  isRagIngestedProductSource,
} from '../utils/isRagIngestedProductSource'
import {
  formatClassificationDisplayValue,
  normalizeClassificationEditableValue,
} from '../utils/productSourceClassificationDisplay'

function ReadOnlyField({
  label,
  value,
  emptyLabel,
  emphasizeLabel = false,
}: {
  label: string
  value: string
  emptyLabel: string
  emphasizeLabel?: boolean
}) {
  const hasValue = value.length > 0
  let labelClassName = 'text-muted-foreground'
  if (emphasizeLabel) {
    labelClassName = 'font-medium text-foreground'
  }

  return (
    <div className='space-y-1.5'>
      <Label className={labelClassName}>{label}</Label>
      <p className='text-sm text-foreground'>
        {hasValue && value}
        {!hasValue && (
          <span className='text-muted-foreground'>{emptyLabel}</span>
        )}
      </p>
    </div>
  )
}

function ClassificationEditFields({
  sourceId,
  classification,
  purpose,
  isPending,
  onClassificationChange,
  onPurposeChange,
}: {
  sourceId: string
  classification: string
  purpose: string
  isPending: boolean
  onClassificationChange: (value: string) => void
  onPurposeChange: (value: string) => void
}): ReactElement {
  const { t } = useTranslation('productCatalog')

  return (
    <>
      <div className='space-y-1.5'>
        <Label htmlFor={`classification-${sourceId}`}>
          {t('sources.classification.documentTypeLabel')}
        </Label>
        <Input
          id={`classification-${sourceId}`}
          value={classification}
          onChange={(event) => onClassificationChange(event.target.value)}
          placeholder={t('sources.classification.documentTypePlaceholder')}
          disabled={isPending}
        />
      </div>
      <div className='space-y-1.5'>
        <Label htmlFor={`purpose-${sourceId}`} className='font-medium'>
          {t('sources.classification.purposeLabel')}
        </Label>
        <Input
          id={`purpose-${sourceId}`}
          value={purpose}
          onChange={(event) => onPurposeChange(event.target.value)}
          placeholder={t('sources.classification.purposePlaceholder')}
          disabled={isPending}
        />
      </div>
    </>
  )
}

function ClassificationReadOnlyFields({
  classificationDisplay,
  purposeDisplay,
  notSetLabel,
}: {
  classificationDisplay: string
  purposeDisplay: string
  notSetLabel: string
}): ReactElement {
  const { t } = useTranslation('productCatalog')

  return (
    <>
      <ReadOnlyField
        label={t('sources.classification.documentTypeLabel')}
        value={classificationDisplay}
        emptyLabel={notSetLabel}
      />
      <ReadOnlyField
        label={t('sources.classification.purposeLabel')}
        value={purposeDisplay}
        emptyLabel={notSetLabel}
        emphasizeLabel
      />
    </>
  )
}

export function ProductSourceClassificationSheet({
  productId,
  source,
  open,
  onOpenChange,
  isUploading = false,
  deleteDisabled = false,
  onDelete,
  inspectPath: inspectPathProp,
}: {
  productId: string
  source: ProductSource
  open: boolean
  onOpenChange: (open: boolean) => void
  isUploading?: boolean
  deleteDisabled?: boolean
  onDelete: () => Promise<void>
  inspectPath?: string
}) {
  const { t } = useTranslation('productCatalog')
  const { mutateAsync: updateClassification, isPending } =
    useUpdateProductSourceClassification(productId)
  const reuploadControls = useReuploadProductSourceFile(productId)

  const [isEditMode, setIsEditMode] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [classification, setClassification] = useState('')
  const [purpose, setPurpose] = useState('')

  const isClassifiableSource = isRagIngestedProductSource(source)
  const showClassificationFields = isClassifiableSource
  const showInspect = isInspectableProductSource(source)
  const showClassificationSection = showClassificationFields
  const showClassificationEditForm = showClassificationSection && isEditMode
  const showClassificationReadOnly = showClassificationSection && !isEditMode
  const showClassificationEditActions = showClassificationSection && isEditMode
  const showClassificationViewEditAction =
    showClassificationSection && !isEditMode
  const showFailureSection = isProductSourceFailed(source) && !isUploading
  const showDeleteSection = !isEditMode
  const isDeleteDisabled = isUploading || deleteDisabled || isDeleting

  const resetFormFromSource = useCallback(() => {
    setClassification(formatClassificationDisplayValue(source.classification))
    setPurpose(source.classificationPurposeOrUtility ?? '')
  }, [source.classification, source.classificationPurposeOrUtility])

  useEffect(() => {
    if (!open) {
      setIsEditMode(false)
      return
    }
    resetFormFromSource()
  }, [open, resetFormFromSource])

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        setIsEditMode(false)
        resetFormFromSource()
      }
      onOpenChange(nextOpen)
    },
    [onOpenChange, resetFormFromSource]
  )

  const handleCancelEdit = useCallback(() => {
    resetFormFromSource()
    setIsEditMode(false)
  }, [resetFormFromSource])

  const handleDelete = useCallback(async () => {
    setIsDeleting(true)
    try {
      await onDelete()
      onOpenChange(false)
    } finally {
      setIsDeleting(false)
    }
  }, [onDelete, onOpenChange])

  const handleSave = useCallback(async () => {
    const normalizedClassification =
      normalizeClassificationEditableValue(classification)
    const normalizedPurpose = normalizeClassificationEditableValue(purpose)

    const classificationChanged =
      normalizedClassification !==
      normalizeClassificationEditableValue(
        formatClassificationDisplayValue(source.classification)
      )
    const purposeChanged =
      normalizedPurpose !==
      normalizeClassificationEditableValue(
        source.classificationPurposeOrUtility ?? ''
      )

    if (!classificationChanged && !purposeChanged) {
      setIsEditMode(false)
      return
    }

    const updatePayload: {
      sourceId: string
      classification?: string | null
      classificationPurposeOrUtility?: string | null
    } = { sourceId: source.id }

    if (classificationChanged) {
      updatePayload.classification = normalizedClassification
    }
    if (purposeChanged) {
      updatePayload.classificationPurposeOrUtility = normalizedPurpose
    }

    try {
      await updateClassification(updatePayload)
      toast.success(t('sources.classification.saveSuccess'))
      setIsEditMode(false)
    } catch {
      toast.error(t('sources.classification.saveError'))
    }
  }, [
    classification,
    purpose,
    source.classification,
    source.classificationPurposeOrUtility,
    source.id,
    t,
    updateClassification,
  ])

  const classificationDisplay = formatClassificationDisplayValue(
    source.classification
  )
  const purposeDisplay = source.classificationPurposeOrUtility ?? ''
  const inspectPath =
    inspectPathProp ??
    `/product-catalog/${source.productId}/sources/${source.id}`
  const notSetLabel = t('sources.classification.notSet')
  const translateIngestionFailure = (key: string, defaultValue: string) =>
    t(`sources.${key}`, { defaultValue })
  const failureMessage = resolveIngestionFailureText({
    ingestionError: source.ingestionError,
    ingestionErrorCode: source.ingestionErrorCode,
    fileUploadStatus: source.fileUploadStatus,
    sourceContentType: source.type,
    sourceFileName: source.name,
    translate: translateIngestionFailure,
  })

  let saveButtonLabel = t('sources.classification.save')
  if (isPending) {
    saveButtonLabel = t('sources.classification.saving')
  }

  return (
    <>
      <input
        ref={reuploadControls.fileInputRef}
        type='file'
        accept={PRODUCT_SOURCE_FILE_ACCEPT}
        className='hidden'
        onChange={reuploadControls.handleFileInputChange}
      />
      <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side='right'
        className='flex w-full flex-col gap-0 sm:max-w-md'
      >
        <SheetHeader className='border-b pe-10'>
          <SheetTitle>{t('sources.classification.sheetTitle')}</SheetTitle>
          <SheetDescription>{source.name}</SheetDescription>
        </SheetHeader>

        <div className='min-h-0 flex-1 space-y-5 overflow-y-auto p-4'>
          <ReadOnlyField
            label={t('sources.classification.fileNameLabel')}
            value={source.name}
            emptyLabel={notSetLabel}
          />

          {showFailureSection && (
            <section className='space-y-3 border-t pt-5'>
              <Label className='text-muted-foreground'>
                {t('sources.status.failureDetails')}
              </Label>
              <ProductCatalogIngestionStatus
                productId={productId}
                sourceId={source.id}
                status={source.ingestionStatus}
                fileUploadStatus={source.fileUploadStatus}
                filePathInBucket={source.filePathInBucket}
                isUploading={isUploading}
                stage={source.ingestionStage}
                progress={source.ingestionProgress}
                ingestionErrorCode={source.ingestionErrorCode}
                sourceContentType={source.type}
                sourceFileName={source.name}
                partialResult={source.ingestionPartialResult}
                missingPages={source.ingestionMissingPages}
                reuploadControls={reuploadControls}
                onReuploadClick={() => onOpenChange(false)}
              />
              <p className='text-sm whitespace-pre-wrap break-words text-destructive'>
                {failureMessage}
              </p>
            </section>
          )}

          {showClassificationSection && (
            <section className='space-y-5'>
              {showClassificationEditForm && (
                <ClassificationEditFields
                  sourceId={source.id}
                  classification={classification}
                  purpose={purpose}
                  isPending={isPending}
                  onClassificationChange={setClassification}
                  onPurposeChange={setPurpose}
                />
              )}

              {showClassificationReadOnly && (
                <ClassificationReadOnlyFields
                  classificationDisplay={classificationDisplay}
                  purposeDisplay={purposeDisplay}
                  notSetLabel={notSetLabel}
                />
              )}

              {showClassificationEditActions && (
                <div className='flex flex-wrap justify-end gap-2'>
                  <Button
                    type='button'
                    variant='outline'
                    onClick={handleCancelEdit}
                    disabled={isPending}
                  >
                    {t('sources.classification.cancel')}
                  </Button>
                  <Button
                    type='button'
                    onClick={() => {
                      void handleSave()
                    }}
                    disabled={isPending}
                  >
                    {isPending && (
                      <Loader2
                        className='me-2 size-4 animate-spin'
                        aria-hidden
                      />
                    )}
                    {saveButtonLabel}
                  </Button>
                </div>
              )}

              {showClassificationViewEditAction && (
                <div className='flex justify-end'>
                  <Button
                    type='button'
                    variant='outline'
                    onClick={() => setIsEditMode(true)}
                  >
                    <Pencil className='me-2 size-4' aria-hidden />
                    {t('sources.classification.edit')}
                  </Button>
                </div>
              )}
            </section>
          )}

          {showInspect && (
            <section className='space-y-3 border-t pt-5'>
              <div className='space-y-1.5'>
                <Label className='text-muted-foreground'>
                  {t('sources.inspect.sectionTitle')}
                </Label>
                <p className='text-sm text-muted-foreground'>
                  {t('sources.inspect.sectionDescription')}
                </p>
              </div>
              <Button variant='outline' size='sm' className='gap-2' asChild>
                <Link
                  to={inspectPath}
                  onClick={() => {
                    onOpenChange(false)
                  }}
                >
                  <Search className='size-4' aria-hidden />
                  {t('sources.inspect.link')}
                </Link>
              </Button>
            </section>
          )}

          {showDeleteSection && (
            <section className='border-t pt-5'>
              <Button
                type='button'
                variant='destructive'
                onClick={() => {
                  void handleDelete()
                }}
                disabled={isDeleteDisabled}
                aria-label={t('sources.panel.deleteSourceAria')}
              >
                {isDeleting && (
                  <Loader2 className='me-2 size-4 animate-spin' aria-hidden />
                )}
                {!isDeleting && <Trash2 className='me-2 size-4' aria-hidden />}
                {t('common:actions.delete')}
              </Button>
            </section>
          )}
        </div>
      </SheetContent>
    </Sheet>
    </>
  )
}
